/**
 * Trial and Pro Access Helper Functions
 */

/**
 * Check if user has Pro access (either through subscription or active trial)
 * @param {Object} user - User object
 * @returns {boolean} - True if user has Pro access
 */
export const hasProAccess = (user) => {
  if (!user) return false;
  
  // Check if user is Pro
  if (user.subscription_status === 'pro') {
    return true;
  }
  
  // Check if user has active trial
  if (user.trial_end_date) {
    const now = new Date();
    const trialEnd = new Date(user.trial_end_date);
    return now <= trialEnd;
  }
  
  return false;
};

/**
 * Check if user is currently on trial
 * @param {Object} user - User object
 * @returns {boolean} - True if user is on active trial
 */
export const isOnTrial = (user) => {
  if (!user || user.subscription_status === 'pro') return false;
  
  if (user.trial_end_date) {
    const now = new Date();
    const trialEnd = new Date(user.trial_end_date);
    return now <= trialEnd;
  }
  
  return false;
};

/**
 * Get remaining trial days
 * @param {Object} user - User object
 * @returns {number} - Number of days remaining (0 if trial expired or no trial)
 */
export const getTrialDaysRemaining = (user) => {
  if (!user || !user.trial_end_date) return 0;
  
  const now = new Date();
  const trialEnd = new Date(user.trial_end_date);
  
  if (now > trialEnd) return 0;
  
  const diffTime = trialEnd - now;
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays;
};

/**
 * Get remaining trial hours (for more precise countdown)
 * @param {Object} user - User object
 * @returns {number} - Number of hours remaining (0 if trial expired or no trial)
 */
export const getTrialHoursRemaining = (user) => {
  if (!user || !user.trial_end_date) return 0;
  
  const now = new Date();
  const trialEnd = new Date(user.trial_end_date);
  
  if (now > trialEnd) return 0;
  
  const diffTime = trialEnd - now;
  const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
  
  return diffHours;
};

/**
 * Check if trial just expired (for showing one-time modal)
 * @param {Object} user - User object
 * @returns {boolean} - True if trial expired and user hasn't upgraded
 */
export const isTrialJustExpired = (user) => {
  if (!user || user.subscription_status === 'pro') return false;
  
  // Trial was used but is now expired
  if (user.trial_used && user.trial_end_date) {
    const now = new Date();
    const trialEnd = new Date(user.trial_end_date);
    return now > trialEnd;
  }
  
  return false;
};
